import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import type { ShareProfile, ShareLink, SharePermissions } from '@/types/sharing';

interface ShareState {
  profiles: ShareProfile[];
  links: Record<string, ShareLink[]>; // keyed by property_id
  isLoading: boolean;
  error: string | null;

  fetchProfiles: () => Promise<void>;
  fetchLinks: (propertyId: string) => Promise<void>;
  createProfile: (data: { name: string; icon: string; permissions: SharePermissions }) => Promise<ShareProfile>;
  updateProfile: (id: string, data: { name?: string; icon?: string; permissions?: SharePermissions }) => Promise<void>;
  deleteProfile: (id: string) => Promise<void>;
  createLink: (data: {
    property_id: string;
    profile_id?: string | null;
    custom_permissions?: SharePermissions | null;
    name?: string | null;
    expires_at?: string | null;
  }) => Promise<ShareLink>;
  deleteLink: (id: string, propertyId: string) => Promise<void>;
  clearError: () => void;
}

export const useShareStore = create<ShareState>((set) => ({
  profiles: [],
  links: {},
  isLoading: false,
  error: null,

  fetchProfiles: async () => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('share_profiles')
        .select('*')
        .order('created_at', { ascending: true });

      if (error) throw error;
      set({ profiles: data || [], isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Fehler beim Laden der Profile';
      set({ error: message, isLoading: false });
    }
  },

  fetchLinks: async (propertyId) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('share_links')
        .select('*, profile:share_profiles(*)')
        .eq('property_id', propertyId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set((state) => ({
        links: { ...state.links, [propertyId]: data || [] },
        isLoading: false,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Fehler beim Laden der Links';
      set({ error: message, isLoading: false });
    }
  },

  createProfile: async (data) => {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) throw new Error('Nicht angemeldet');

    const { data: profile, error } = await supabase
      .from('share_profiles')
      .insert({ ...data, user_id: userData.user.id })
      .select()
      .single();

    if (error) throw error;

    set((state) => ({ profiles: [...state.profiles, profile] }));
    return profile;
  },

  updateProfile: async (id, data) => {
    const { error } = await supabase
      .from('share_profiles')
      .update(data)
      .eq('id', id);

    if (error) throw error;

    set((state) => ({
      profiles: state.profiles.map((p) => (p.id === id ? { ...p, ...data } : p)),
    }));
  },

  deleteProfile: async (id) => {
    const { error } = await supabase
      .from('share_profiles')
      .delete()
      .eq('id', id);

    if (error) throw error;

    set((state) => ({ profiles: state.profiles.filter((p) => p.id !== id) }));
  },

  createLink: async (data) => {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) throw new Error('Nicht angemeldet');

    const { data: link, error } = await supabase
      .from('share_links')
      .insert({ ...data, created_by: userData.user.id })
      .select('*, profile:share_profiles(*)')
      .single();

    if (error) throw error;

    set((state) => ({
      links: {
        ...state.links,
        [data.property_id]: [link, ...(state.links[data.property_id] || [])],
      },
    }));
    return link;
  },

  deleteLink: async (id, propertyId) => {
    const { error } = await supabase
      .from('share_links')
      .delete()
      .eq('id', id);

    if (error) throw error;

    set((state) => ({
      links: {
        ...state.links,
        [propertyId]: (state.links[propertyId] || []).filter((l) => l.id !== id),
      },
    }));
  },

  clearError: () => set({ error: null }),
}));
